<template>
  <UIPanel
    v-model="clearedSearch"
    v-model:storage="searchStorage"
    :disabled
    placeholder="Search positions..."
  >
    <template #default="{ size, ButtonExport }">
      <UISort
        v-model="clearedSort"
        v-model:storage="sortStorage"
        :disabled
        :size
        :fields="sortFields"
      />
      <component
        :is="ButtonExport"
        :disabled
        @click="handleExport"
      />
    </template>
  </UIPanel>
  <UIGridSkeleton
    v-if="loading"
    :columns
    :sm
    wrapped
    selectable
    message="Loading positions..."
  />
  <UIGrid
    v-else
    v-model:sort="sortStorage"
    :items
    :columns
    :sm
    wrapped
    scrollable
  >
    <template #actions="{ item, size }">
      <UIButton
        v-if="item?.allposit_id"
        label="View Transaction"
        :leading="PencilIcon"
        variant="secondary"
        fill="light"
        :size
        @click="handleClickViewTransaction(item)"
      />
      <UIButton
        v-if="item"
        label="Source view"
        :leading="ArrowTopRightOnSquareIcon"
        variant="gray"
        fill="light"
        :size
        @click="handleOpenJsonClick(item)"
      />
    </template>
  </UIGrid>
  <UIPagination
    v-if="!loading"
    :disabled
    :page-no
    :page-size="pagination?.page_size"
    :total="pagination?.total"
    @click:page="handleClickPage"
  />
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, onWatcherCleanup, ref } from 'vue'
import { debouncedWatch, watchPausable } from '@vueuse/core'
import { isEqual } from 'lodash'

import { LinkedDataPosition, PaginatedMeta, Sort } from '@types'

import { DEBOUNCE_DELAY, NOTIFICATION_DELAY } from '@/const'

import { stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'
import { downloadAsFile, handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useLinkedDataPositionsStore } from '@/store/linkedData/positions'
import { useModalsStore } from '@/store/modals'
import { useTransactionsBunchStore } from '@/store/transactions/bunch'

import { UIPanel, UIPagination } from '@ui/core'
import { UIButton } from '@ui/buttons'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UISort } from '@ui/controllers'
import {
  ArrowTopRightOnSquareIcon,
  PencilIcon,
} from '@heroicons/vue/24/outline'
import AccountConnectorPositionsJSON from './AccountConnectorPositionsJSON.vue'

type Props = {
  linkedAccountId?: string | null
}

const props = defineProps<Props>()

defineOptions({
  name: 'AccountPositions',
})

const linkedDataPositionsStore = useLinkedDataPositionsStore()
const modalsStore = useModalsStore()
const transactionsBunchStore = useTransactionsBunchStore()
const { progress, update } = useNotifications()

const items = ref<LinkedDataPosition[]>([])
const pagination = ref<PaginatedMeta>()

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sortStorage = ref<Sort[]>()
const clearedSort = ref<Sort[]>()

const pageNo = ref(0)

const loading = ref(true)
const disabled = ref(true)

const sm = '1fr 1fr'
const columns = computed(() => [
  {
    name: 'description',
    caption: 'Description',
  },
  {
    name: 'ticker',
    caption: 'Symbol',
  },
  {
    name: 'price',
    caption: 'Price',
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    formatter: numberFormat,
    default: '.5fr',
  },
  {
    name: 'quantity',
    caption: 'Qty',
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    formatter: numberFormat,
    default: '.5fr',
  },
  {
    name: 'value',
    caption: 'Value',
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    formatter: numberFormat,
    default: '.5fr',
  },
  {
    name: 'as_of_date',
    caption: 'As of',
    formatter: (value: string) => stringToLocalDateString(value),
    default: '7rem',
  },
])

const sortFields = computed(() => columns.value.map(column => ({
  value: column.name,
  label: column.caption,
})))

const fetchOptions = computed(() => ({
  search: clearedSearch.value,
  sort: clearedSort.value,
  account_id: props.linkedAccountId || undefined,
}))

const handleClickViewTransaction = (
  position: LinkedDataPosition,
) => {
  let instance: any
  if (position.allposit_id) {
    instance = transactionsBunchStore.getElementById(
      position.allposit_id,
    )
  } else {
    instance = transactionsBunchStore.createElement()
    instance.set({
      linked_id: position.id,
    })
  }
  if (!instance) return
  const modalInstance = modalsStore.init(instance.id, instance, { allowTray: true })
  modalInstance?.open(modalsStore.getZIndex())
}

const handleOpenJsonClick = async (position: LinkedDataPosition) => {
  const key = `${position.description} JSON [${position.id}]`
  const modalInstance = modalsStore.init(key, markRaw(AccountConnectorPositionsJSON), { allowTray: true })
  modalInstance?.open(modalsStore.getZIndex(), {
    position,
  })
}

const fetch = async (signal?: AbortSignal) => {
  try {
    loading.value = true

    const options = {
      ...fetchOptions.value,
      page_no: pageNo.value,
    }
    const { data, meta } = await linkedDataPositionsStore.fetch(options, false, signal)
    items.value = data
    pagination.value = meta
    disabled.value = false
  } catch (e) {
    handleCatchedError(e as string, fetchOptions.value)
  } finally {
    loading.value = false
  }
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

const handleExport = async () => {
  const nid = await progress({
    message: 'Exporting positions',
  })
  try {
    const result = await linkedDataPositionsStore.export(fetchOptions.value)
    downloadAsFile(result, 'asset_positions', 'csv')
    await update(
      nid,
      {
        type: 'success',
        message: 'Positions exported',
      },
      NOTIFICATION_DELAY,
    )
  } catch (e) {
    handleCatchedError(e as string)
    await update(nid, {
      type: 'error',
      message: 'Failed to export positions',
    })
  }
}

const { pause, resume } = watchPausable(pageNo, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

debouncedWatch(() => ({
  search: clearedSearch.value,
  sort: clearedSort.value,
}),
(value, prev) => {
  if (isEqual(value, prev)) return
  pause()
  pageNo.value = 0
  fetch()
  nextTick(resume)
},
{
  deep: true,
  immediate: true,
  debounce: DEBOUNCE_DELAY,
},
)
</script>
